import { useGetAvlItemsQuery } from '@/services/approvedVendorList';
import { Box, FormikRadioGroup, FormLabel } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { AvlFilterOption, VendorEquipmentTypes } from 'types';

import FormSection from '../Forms/FormSection';

import { filterDcEligibleAvlItems, mapAllAvlManufacturerOptions, Option } from './AvlFormUtils';
import DomesticContentMountingEquipSelectionSection from './DomesticContentMountingEquipSelectionSection';
import { MountingHardwareProps } from './MountingHardwareFormSection';
import { MountingTypeAlert } from './MountingTypeAlert';

export interface DomesticContentMountingHardwareProps extends MountingHardwareProps {
  avlFilterOption?: AvlFilterOption;
  isOptionalForNtp?: boolean;
  readOnly?: boolean;
  allowDeleteMountingHardware?: boolean;
  hideMountingType?: boolean;
  isItcSystemDesignCompleted?: boolean;
}

const mountingTypeOptions: Option[] = [
  { value: 'roof', label: 'Roof Mount', id: 'roof' },
  { value: 'ground', label: 'Ground Mount (Ineligible for Domestic Content)', id: 'ground' },
  { value: 'combination', label: 'Roof/Ground Combination (Ineligible for Domestic Content)', id: 'combination' },
];

export const DomesticContentMountingHardwareSection = ({
  className,
  hideTitleAndDescription = false,
  isLoading,
  boxProps,
  programType,
  avlFilterOption,
  isOptionalForNtp = true,
  readOnly = false,
  allowDeleteMountingHardware = true,
  hideMountingType = false,
  isItcSystemDesignCompleted = false,
  orgAlias,
}: DomesticContentMountingHardwareProps) => {
  const { isSubmitting } = useFormikContext<any>();
  const { id: accountId } = useParams<{ id: string }>();
  // Server derives effectiveDate from accountId; never sent by the client.
  const { data: mountingData, isLoading: isAppSettingsLoading } = useGetAvlItemsQuery(
    accountId
      ? { accountId, equipmentType: VendorEquipmentTypes.mounting }
      : { programType, orgAlias, equipmentType: VendorEquipmentTypes.mounting },
  );
  const mounting = useMemo(() => {
    const items = mountingData?.data ?? [];
    // V2 has no server-side avlFilterOption; dcEligibleOnly is applied here instead.
    return avlFilterOption === AvlFilterOption.dcEligibleOnly
      ? filterDcEligibleAvlItems(items, VendorEquipmentTypes.mounting)
      : items;
  }, [mountingData, avlFilterOption]);

  const isAnythingLoadingOrSubmitting = isLoading || isAppSettingsLoading || isSubmitting;
  const boxValues = boxProps ?? { width: '100%', margin: 'sm' };
  const shouldHideMountingType = hideMountingType || isItcSystemDesignCompleted;

  const manufacturerOptions = useMemo(() => mapAllAvlManufacturerOptions(mounting), [mounting]);

  return (
    <FormSection
      {...(!hideTitleAndDescription
        ? { title: 'Mounting', description: isOptionalForNtp ? 'Optional for NTP' : undefined }
        : { className })}
    >
      <Box
        direction="column"
        childGap={{ base: 'lg', desktop: 'xl' }}
        width={boxValues.width}
        margin={boxValues.margin}
        gap="lg"
      >
        {!shouldHideMountingType && (
          <Box childGap={{ base: 'lg', desktop: 'lg' }}>
            <FormLabel inputId="mountingType">Mounting Type</FormLabel>
            <Field
              label="Mounting Type"
              name="mountingType"
              id="mountingType"
              component={FormikRadioGroup}
              options={mountingTypeOptions}
              isDisabled={isAnythingLoadingOrSubmitting || readOnly}
            />
          </Box>
        )}
        {hideMountingType && <MountingTypeAlert />}
        <Box childGap={{ base: 'lg', desktop: 'xl' }}>
          <DomesticContentMountingEquipSelectionSection
            manufacturerOptions={manufacturerOptions}
            userOnlyHasReadPermissions={readOnly}
            allowDeleteMountingHardware={allowDeleteMountingHardware}
          />
        </Box>
      </Box>
    </FormSection>
  );
};
